import type Store from '@ember-data/store';
import Controller from '@ember/controller';
import { get } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import ENV from 'donor-elf-web/config/environment';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import objectUtils from 'donor-elf-web/lib/object-utils';
import type FundModel from 'donor-elf-web/models/fund';
import type FundsService from 'donor-elf-web/services/funds-service';
import { Changeset } from 'ember-changeset';
import FileSaver from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';
import { TrackedArray } from 'tracked-built-ins';

const PAGE_SIZE = 20;

export default class PbtDisbursementSummaryController extends Controller {
  declare model: { funds: FundModel[] };

  @service declare fundsService: FundsService;
  @service declare store: Store;

  @tracked month: string = '';
  @tracked year: string = '';
  @tracked company: string = '';
  @tracked fundName: string = '';
  @tracked loading = false;
  @tracked filterExpanded = false;
  @tracked filterChangeset?: Changeset = undefined;
  @tracked loadingProgressBarStyle: SafeString | string = '';
  @tracked sortBy = ['fund.name:asc'];
  @tracked error: any = null;
  @tracked records: any[] = [];
  @tracked companyOptions: any[] = [];

  queryParams = ['month', 'year', 'company', 'fundName'];
  monthOptions = [
    {
      label: 'January',
      val: '1',
    },
    {
      label: 'February',
      val: '2',
    },
    {
      label: 'March',
      val: '3',
    },
    {
      label: 'April',
      val: '4',
    },
    {
      label: 'May',
      val: '5',
    },
    {
      label: 'June',
      val: '6',
    },
    {
      label: 'July',
      val: '7',
    },
    {
      label: 'August',
      val: '8',
    },
    {
      label: 'September',
      val: '9',
    },
    {
      label: 'October',
      val: '10',
    },
    {
      label: 'November',
      val: '11',
    },
    {
      label: 'December',
      val: '12',
    },
  ];

  get rootURL() {
    return ENV.rootURL;
  }

  get sortedRecords() {
    return sortByProperties(this.records, this.sortBy);
  }

  get filteredSortedRecords() {
    if (isPresent(this.fundName)) {
      let fundNameLowerCase = this.fundName.toLowerCase();
      return this.sortedRecords.filter((record) => {
        return record.fundNameLowerCase.indexOf(fundNameLowerCase) >= 0;
      });
    } else {
      return this.sortedRecords;
    }
  }

  get fundsById() {
    let fundsById: Record<string, FundModel> = {};

    this.model.funds.forEach((fund) => {
      fundsById[fund.id] = fund;
    });

    return fundsById;
  }

  get toDateMoment() {
    return moment({
      year: Number(get(this, 'filterChangeset.year')),
      month: Number(get(this, 'filterChangeset.month')) - 1,
      day: 1,
    }).endOf('month');
  }

  fetchData() {
    this.loading = true;
    this.records = new TrackedArray();

    const filter = {
      month: this.month,
      year: this.year,
      company: this.company,
    };

    let filterChangeset = Changeset(filter);
    this.filterChangeset = filterChangeset;

    this.fetchDataPage(1, filter);
  }

  get fetchTotalPages() {
    return Math.ceil(this.model.funds.length / PAGE_SIZE);
  }

  async fetchDataPage(page: number, filter: any) {
    let loadingPercentage = 2;

    if (this.fetchTotalPages && page > 1) {
      loadingPercentage = Math.round(((page - 1) * 100) / this.fetchTotalPages);
    }

    if (loadingPercentage < 2) {
      loadingPercentage = 2;
    }

    this.loadingProgressBarStyle = htmlSafe(`width: ${loadingPercentage}%`);

    const offset = (page - 1) * PAGE_SIZE;
    filter.fundIds = this.model.funds
      .slice(offset, offset + PAGE_SIZE)
      .map((x) => x.id);
    filter.attachCompanyValues = page === 1;

    try {
      await this.fundsService.confirmValidFundToken();

      const report = await this.store.query(
        'custom-reports/disbursement-availability-report',
        {
          filter: objectUtils.decamelizeKeys(filter),
        },
      );

      report.slice().forEach((record: any) => {
        let fund = this.fundsById[record.fundId]!;
        this.records.push({
          fund,
          fundNameLowerCase: fund.nameLowerCase,
          report: record,
        });
      });

      let companyValues = (report as any).meta?.company_values;
      if (isPresent(companyValues)) {
        let companyOptions = [
          {
            label: 'All',
            val: '',
          },
        ].concat(
          companyValues.map((company: any) => {
            return {
              label: company,
              val: company,
            };
          }),
        );

        this.companyOptions = companyOptions;
      }

      if (page < this.fetchTotalPages) {
        this.fetchDataPage(page + 1, filter);
      } else {
        this.loading = false;
      }
    } catch (error: any) {
      this.error = error;
      this.loading = false;
    }
  }

  get yearOptions() {
    const options = [];
    const thisYear = new Date().getFullYear();

    for (let year = thisYear - 4; year <= thisYear; year++) {
      options.push({
        label: year.toString(),
        val: year.toString(),
      });
    }

    return options;
  }

  get actionsMenuItems() {
    return [
      {
        label: this.filterExpanded ? 'Collapse Filter' : 'Adjust Filter',
        svgJar: 'filter',
        onClick: this.toggleExpanded,
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  get filterMessage() {
    const messages = [];
    messages.push(`${this.toDateMoment.format('MMMM YYYY')}`);

    if (isPresent(this.company)) {
      messages.push(`Company: ${this.company}`);
    } else {
      messages.push('Company: All');
    }

    return messages.join(', ');
  }

  applyFilter = () => {
    const filterChangeset = this.filterChangeset;

    if (get(filterChangeset, 'isPristine')) {
      alert('No filter values have changed');
      return;
    }

    this.month = this.filterChangeset.get('month');
    this.year = this.filterChangeset.get('year');
    this.company = this.filterChangeset.get('company');
    this.filterExpanded = false;

    this.fetchData();
  };

  applyFundNameFilter = (fundName: string) => {
    console.log('applyFundNameFilter', this, fundName);
    this.fundName = fundName;
  };

  download = () => {
    const rows = [];
    rows.push([
      'Fund',
      'Balance',
      'Expenses Not Yet Reimbursed',
      'Advances Not Accounted For',
      'Available For Disbursement',
    ]);

    this.sortedRecords.forEach((record) => {
      rows.push([
        record.fund.name,
        record.report.balance,
        record.report.notReimbursed,
        record.report.advances,
        record.report.availableForDisbursement,
      ]);
    });

    let csv = Papa.unparse(rows);
    let blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'disbursement-summary.csv', true);
  };

  templateDidInsert = () => {
    this.month = (new Date().getMonth() + 1).toString();
    this.year = new Date().getFullYear().toString();

    this.fetchData();
  };

  toggleExpanded = () => {
    this.filterExpanded = !this.filterExpanded;
  };
}
